import React, { useEffect, useState } from 'react';
import './Users.css';

// Data
// Components
import Table from '../../../../components/Table/Table';
import UserSearchBar, {
  EMPTY_USER_FILTER,
  getUserFilterFromSearchParams,
  getUserFilterSearchParams,
} from '../../../../components/Users/Search';

// Get users
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useSearchParams } from 'react-router-dom';
import { getPremiumSubscriberUserIds } from '../../../../utils/firebase/getPremiumSubscriberUserIds';
import {
  capitalizeFirstCharacter,
  sortUsersByCreatedAtDesc,
  userMatchesSearchFilter,
} from '../../../../utils/userSearch';

const mapUsersFromSnapshot = (querySnapshot) => querySnapshot.docs.map((doc) => ({
  id: doc.id,
  ...doc.data(),
}));

const fetchExactMatchUsers = async (filter) => {
  const activeFilter = getUserFilterSearchParams(filter);

  if (Object.keys(activeFilter).length === 0) {
    return [];
  }

  const queryConstraints = Object.entries(activeFilter).map(([field, value]) => where(field, '==', value));
  const usersQuery = query(collection(db, 'users'), ...queryConstraints, limit(50));
  const querySnapshot = await getDocs(usersQuery);

  return mapUsersFromSnapshot(querySnapshot);
};

const getCapitalizedFallbackFilter = (filter = {}) =>
  Object.entries(filter).reduce((nextFilter, [field, value]) => {
    const capitalizedValue = capitalizeFirstCharacter(value);

    if (capitalizedValue) {
      nextFilter[field] = capitalizedValue;
    }

    return nextFilter;
  }, {});

const annotatePremiumSubscribers = async (users = []) => {
  if (!users.length) {
    return [];
  }

  const premiumSubscriberUserIds = await getPremiumSubscriberUserIds(users.map((user) => user.id));

  return users.map((user) => ({
    ...user,
    isPremiumSubscriberActive: premiumSubscriberUserIds.has(user.id),
  }));
};

const withDefaultPremiumFlag = (users = []) =>
  users.map((user) => ({
    ...user,
    isPremiumSubscriberActive: false,
  }));

const fetchUsersFromFirestore = async (filter = EMPTY_USER_FILTER) => {
  const activeFilter = getUserFilterSearchParams(filter);

  if (Object.keys(activeFilter).length === 0) {
    const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(50));
    const querySnapshot = await getDocs(usersQuery);

    return mapUsersFromSnapshot(querySnapshot);
  }

  const exactMatches = await fetchExactMatchUsers(activeFilter);
  if (exactMatches.length) {
    return exactMatches;
  }

  const capitalizedFallbackFilter = getCapitalizedFallbackFilter(activeFilter);
  const shouldTryCapitalizedFallback = Object.keys(capitalizedFallbackFilter).some(
    (field) => capitalizedFallbackFilter[field] !== activeFilter[field]
  );

  if (shouldTryCapitalizedFallback) {
    const capitalizedMatches = await fetchExactMatchUsers(capitalizedFallbackFilter);
    if (capitalizedMatches.length) {
      return capitalizedMatches;
    }
  }

  const querySnapshot = await getDocs(collection(db, 'users'));

  return mapUsersFromSnapshot(querySnapshot)
    .filter((user) => userMatchesSearchFilter(user, activeFilter))
    .sort(sortUsersByCreatedAtDesc)
    .slice(0, 50);
};

const Users = () => {
  const [usersDB, setUsersDB] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(() => getUserFilterFromSearchParams(searchParams));

  const columns = [
    { label: "First name", accessor: "firstName", sortable: true, date: false },
    { label: "Last name", accessor: "lastName", sortable: false, date: false },
    { label: "Email", accessor: "email", sortable: true, date: false },
    { label: "User ID", accessor: "id", sortable: true, date: false },
    { label: "Phone number", accessor: "phoneNumber", sortable: true, date: false },
    { label: "Joined", accessor: "createdAt", sortable: true, date: true, sortbyOrder: "desc" },
    { label: "Edit", accessor: "edit", sortable: false, date: false },
  ];


  useEffect(() => {
    let cancelled = false;
    const nextFilter = getUserFilterFromSearchParams(searchParams);
    setFilter(nextFilter);

    const loadUsers = async () => {
      setLoading(true);

      try {
        const users = await fetchUsersFromFirestore(nextFilter);

        if (cancelled) {
          return;
        }

        setUsersDB(withDefaultPremiumFlag(users));
        setLoading(false);

        if (!users.length) {
          return;
        }

        try {
          const premiumAnnotatedUsers = await annotatePremiumSubscribers(users);

          if (!cancelled) {
            setUsersDB(premiumAnnotatedUsers);
          }
        } catch (premiumError) {
          console.error('Error loading premium subscriber highlights:', premiumError);
        }
      } catch (error) {
        console.error('Error fetching users:', error);

        if (!cancelled) {
          alert(error?.message || 'Unable to load users.');
          setLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const handleFilterChange = (event) => {
    setFilter((currentFilter) => ({
      ...currentFilter,
      [event.target.name]: event.target.value,
    }));
  };

  const applyFilter = (event) => {
    event.preventDefault();
    const nextSearchParams = getUserFilterSearchParams(filter);

    // If no filter, give an error message
    if (Object.keys(nextSearchParams).length === 0) {
      alert('Please enter a filter');
      return;
    }

    setSearchParams(nextSearchParams);
  };

  const handleFetchLatestUsers = () => {
    setSearchParams({});
  };

  return (
    <div className='users__container'>
      <h1 className='users__heading'>Users</h1>

      <UserSearchBar
        filter={filter}
        onFilterChange={handleFilterChange}
        onSubmit={applyFilter}
      />

      <button className='users__button' onClick={handleFetchLatestUsers}>
        Fetch new users
      </button>

      {loading ?
        <div>Loading...</div>
        :
        <Table
          caption="List of users registered"
          data={usersDB}
          columns={columns}
          path="users"
        />
      }
    </div>
  );
};

export default Users;
